/**
 * Types for in-person students.
 */
import type { ProfileGenderForm, WeeklyAvailabilitySlot } from '@/types/user.types';

/** Aligned with ednov-backend user-status: student. */
export type StudentAccountStatus = 'registered' | 'accompanied' | 'deactivated';

/** In-person student list/detail row (`GET /students`). */
export interface Student {
  id: string;
  companyId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  phone: string | null;
  photoUrl: string | null;
  status: StudentAccountStatus;
  userEstablishments?: Array<{
    establishmentId: string;
    establishment: {
      id: string;
      name: string;
      uaiCode?: string;
    };
  }>;
  userRoles?: Array<{
    roleId: string;
    role: { code: string; label: string };
  }>;
  userProfiles?: Array<{
    profileId?: string | null;
    subjects?: unknown[] | null;
    availability?: Record<string, unknown> | null;
    isMinor?: boolean;
    hasSpecialNeeds?: boolean;
    specialNeedsType?: string | null;
    specialNeedsNote?: string | null;
    isDisabled?: boolean;
    disabilityTypes?: string[] | null;
    disabilityOtherNote?: string | null;
    dateOfBirth?: string | null;
    gender?: 'male' | 'female' | 'other' | null;
    address?: string | null;
    addressLine1?: string | null;
    department?: string | null;
    postalCode?: string | null;
    city?: string | null;
    country?: string | null;
    levelId?: string | null;
    level?: {
      id: string;
      label: string;
    } | null;
    schoolClassId?: string | null;
    schoolClass?: {
      id: string;
      name: string;
    } | null;
    medicalNotes?: string | null;
    userProfileSpecialties?: Array<{
      specialty: {
        id: string;
        code: string;
        label: string;
      };
    }> | null;
  }>;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  deactivationDate?: string | null;
  deactivationReason?: string | null;
  deactivationReasonNote?: string | null;
  /** Attendance % from session presence (list enrichment, not a stored column). */
  presenceRate?: number | null;
}

/** Body for `POST /students` (in-person). */
export interface CreateStudentDto {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other';
  addressLine1?: string;
  department?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  levelId?: string;
  roleIds: string[];
  establishmentIds: string[];
  schoolClassId?: string;
  subjects?: string[];
  availability?: {
    weekly: Array<{
      day: number;
      start?: string;
      end?: string;
    }>;
  };
  hasSpecialNeeds?: boolean;
  specialNeedsType?: string;
  specialNeedsNote?: string;
  isDisabled?: boolean;
  disabilityTypes?: string[];
  disabilityOtherNote?: string;
  medicalNotes?: string;
  specialtyIds?: string[];
  programIds?: string[];
  /** Curriculum track label when the program requires one. */
  coursePath?: string;
}

/** Body for `PATCH /students/:id` (in-person). */
export interface UpdateStudentDto {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  photoUrl?: string;
  status?: StudentAccountStatus;
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other';
  addressLine1?: string;
  department?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  levelId?: string;
  establishmentIds?: string[];
  schoolClassId?: string | null;
  subjects?: string[];
  availability?: CreateStudentDto['availability'];
  hasSpecialNeeds?: boolean;
  specialNeedsType?: string;
  specialNeedsNote?: string;
  isDisabled?: boolean;
  disabilityTypes?: string[];
  disabilityOtherNote?: string;
  deactivationReason?: string;
  deactivationReasonNote?: string;
  exitDate?: string;
  medicalNotes?: string;
  specialtyIds?: string[];
  programIds?: string[];
  coursePath?: string;
}

/** One row error from `POST /users/import-csv`. */
export interface ImportCsvError {
  rowNumber: number;
  email?: string;
  reason: string;
  rawRow?: Record<string, string>;
}

/** CSV import summary (imported / skipped / invalid). */
export interface ImportCsvResult {
  totalRows: number;
  importedCount: number;
  skippedExistingEmailCount: number;
  invalidCount: number;
  errors: ImportCsvError[];
}

/** Local create-student form shape. */
export type CreateStudentFormState = {
  firstName: string;
  lastName: string;
  displayName: string;
  dateOfBirth: string;
  gender: ProfileGenderForm;
  levelId: string;
  schoolClassId: string;
  addressLine1: string;
  department: string;
  postalCode: string;
  city: string;
  country: string;
  roleIds: string[];
  establishmentIds: string[];
  subjects: string[];
  availability: { weekly: WeeklyAvailabilitySlot[] };
  hasSpecialNeeds: boolean;
  specialNeedsType: string;
  specialNeedsNote: string;
  isDisabled: boolean;
  disabilityTypes: string[];
  disabilityOtherNote: string;
  medicalNotes: string;
  status: string;
  specialtyIds: string[];
  programIds: string[];
  coursePath: string;
};

/** Local edit-student form shape. */
export type EditStudentFormState = {
  firstName: string;
  lastName: string;
  displayName: string;
  status: string;
  dateOfBirth: string;
  gender: string;
  levelId: string;
  schoolClassId: string;
  addressLine1: string;
  department: string;
  postalCode: string;
  city: string;
  country: string;
  establishmentIds: string[];
  subjects: string[];
  availability: { weekly: WeeklyAvailabilitySlot[] };
  hasSpecialNeeds: boolean;
  specialNeedsType: string;
  specialNeedsNote: string;
  isDisabled: boolean;
  disabilityTypes: string[];
  disabilityOtherNote: string;
  medicalNotes: string;
  specialtyIds: string[];
  deactivationReasonType: string;
  deactivationReasonOther: string;
  exitDate: string;
  programIds: string[];
  coursePath: string;
};

/** One status change from `GET /users/:id/status-history`. */
export interface StudentStatusHistoryRow {
  id: string;
  studentUserId: string;
  oldStatus: string | null;
  newStatus: string;
  createdAt: string;
}

/** Collective vs individual accompaniment. */
export type StudentAccompanimentType = 'collective' | 'individual';

/** List filters for in-person students. */
export interface StudentFilters {
  search?: string;
  statuses: string[];
  levelIds: string[];
  subjects: string[];
  establishmentId?: string;
  classIds: string[];
  specialtyIds: string[];
  createdFrom?: Date;
  createdTo?: Date;
  isMinor?: boolean;
  hasSpecialNeeds?: boolean;
  accompanimentTypes: StudentAccompanimentType[];
  exitDateFrom?: Date;
  exitDateTo?: Date;
  exitReason?: string;
  availability?: string[];
}

/** Legacy payloads → canonical student statuses. */
export function normalizeStudentAccountStatus(raw: string): StudentAccountStatus {
  if (raw === 'registered' || raw === 'accompanied' || raw === 'deactivated') return raw;
  if (raw === 'active') return 'registered';
  if (raw === 'suspended') return 'deactivated';
  return 'registered';
}
